import { useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './CombatGrid.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Bússola (rosa-dos-ventos) no canto do mapa
//
// Desenhada "deitada no chão", na MESMA perspectiva isométrica da grade: cada
// braço aponta para a direção real do mapa na tela e gira junto com a câmera.
// `north` diz qual lado do mapa (visão de cima do editor) é o Norte.
// ─────────────────────────────────────────────────────────────────────────────

const SIZE = 104;          // px do canvas (quadrado)
const R    = 34;           // raio da rosa
const FLAT = 0.6;          // achatamento vertical (perspectiva do chão)

interface CompassProps {
  /** Rotação atual da câmera, em radianos (a mesma do CombatGrid). */
  rotation: number;
  /** Lado do mapa que é o Norte: 0=topo, 90=direita, 180=baixo, 270=esquerda. */
  north: number;
}

export function Compass({ rotation, north }: CompassProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { t, language } = useTranslation();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = SIZE * dpr;
    canvas.height = SIZE * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, SIZE, SIZE);

    const cx = SIZE / 2, cy = SIZE / 2 + 2;
    const cos = Math.cos(rotation), sin = Math.sin(rotation);
    const northRad = (north * Math.PI) / 180;

    /**
     * Ponto na tela para a direção de bússola `theta` (0 = Norte, horário) a
     * uma distância `r`. Direção no grid = (sin θ, −cos θ) — y cresce para
     * baixo no editor — depois rotação da câmera + projeção isométrica.
     */
    const at = (theta: number, r: number): [number, number] => {
      const a = northRad + theta;
      const gx = Math.sin(a), gy = -Math.cos(a);
      const rx = gx * cos - gy * sin, ry = gx * sin + gy * cos;
      return [cx + ((rx - ry) / Math.SQRT2) * r, cy + ((rx + ry) / Math.SQRT2) * r * FLAT];
    };

    const ring = (r: number) => {
      ctx.beginPath();
      for (let i = 0; i <= 48; i++) {
        const [x, y] = at((i / 48) * Math.PI * 2, r);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
    };

    // Disco de pergaminho com borda escura (sombra leve embaixo).
    ctx.save();
    ctx.translate(0, 3);
    ring(R * 1.05);
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fill();
    ctx.restore();
    ring(R * 1.05);
    ctx.fillStyle = '#d8b98c';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#5a3a22';
    ctx.stroke();
    ring(R * 0.9);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(90,58,34,0.6)';
    ctx.stroke();

    // Braço em losango: metade clara + metade escura dá o relevo clássico.
    const arm = (theta: number, len: number, halfW: number, light: string, dark: string) => {
      const tip = at(theta, len);
      const l = at(theta - Math.PI / 2, halfW), r = at(theta + Math.PI / 2, halfW);
      const c: [number, number] = [cx, cy];
      ctx.fillStyle = light;
      ctx.beginPath(); ctx.moveTo(...c); ctx.lineTo(...l); ctx.lineTo(...tip); ctx.closePath(); ctx.fill();
      ctx.fillStyle = dark;
      ctx.beginPath(); ctx.moveTo(...c); ctx.lineTo(...r); ctx.lineTo(...tip); ctx.closePath(); ctx.fill();
    };

    // Colaterais (NE/SE/SO/NO) curtos, depois os 4 cardeais por cima.
    for (let k = 0; k < 4; k++) {
      arm(Math.PI / 4 + (k * Math.PI) / 2, R * 0.55, R * 0.1, '#b08a5e', '#6e4a2c');
    }
    for (let k = 0; k < 4; k++) {
      const isNorth = k === 0;
      arm((k * Math.PI) / 2, R * 0.88, R * 0.16,
        isNorth ? '#d0453a' : '#8f5b34',
        isNorth ? '#8e211a' : '#4e2f18');
    }
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fillStyle = '#f3e3c3';
    ctx.fill();
    ctx.strokeStyle = '#5a3a22';
    ctx.stroke();

    // Letras (sempre em pé), fora do disco.
    const labels = [t('compass.N'), t('compass.E'), t('compass.S'), t('compass.W')];
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    labels.forEach((label, k) => {
      const [x, y] = at((k * Math.PI) / 2, R * 1.32);
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.strokeText(label, x, y);
      ctx.fillStyle = k === 0 ? '#ff6b5e' : '#f3e3c3';
      ctx.fillText(label, x, y);
    });
  }, [rotation, north, language]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={ref}
      className={styles.compass}
      style={{ width: SIZE, height: SIZE }}
      aria-label={t('compass.label')}
      role="img"
    />
  );
}
